import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { configureHttpClient } from '@/shared/libs/http-client';
import { AuthContext } from './AuthContext';
import { sessionManager } from './libs/session-manager';
import { loginRequest, refreshRequest } from './services/auth.service';
import type { AuthResponse, LoginCredentials } from './types/auth.types';

export interface AuthProviderProps {
  readonly children: ReactNode;
}

/**
 * Restauration silencieuse au boot : POST /api/v2/auth/refresh avec le refresh
 * token persisté, puis setSession (l'access token reste en mémoire). En cas de
 * token invalide/expiré, clearSession() nettoie tout → retour /login propre.
 */
async function restorePersistedSession(): Promise<void> {
  const refreshToken = sessionManager.getPersistedRefreshToken();
  if (!refreshToken) return;
  try {
    const response = await refreshRequest(refreshToken);
    sessionManager.setSession(response);
  } catch {
    sessionManager.clearSession();
  }
}

/** Promise partagée : évite deux POST /refresh concurrents en dev (StrictMode). */
let restoreInFlight: Promise<void> | null = null;

export function AuthProvider({ children }: AuthProviderProps) {
  const session = useSyncExternalStore(
    sessionManager.subscribe,
    sessionManager.getSnapshot,
    sessionManager.getSnapshot,
  );
  const [isInitializing, setIsInitializing] = useState(() =>
    sessionManager.hasPersistedRefreshToken(),
  );

  // Restauration au démarrage : un refresh token persisté (rechargement de page)
  // déclenche un refresh silencieux avant tout rendu de la garde RequireAuth.
  useEffect(() => {
    let cancelled = false;

    if (sessionManager.hasPersistedRefreshToken()) {
      if (!restoreInFlight) {
        restoreInFlight = restorePersistedSession().finally(() => {
          restoreInFlight = null;
        });
      }
      void restoreInFlight
        .finally(() => {
          if (!cancelled) setIsInitializing(false);
        })
        // Sécurité : ne jamais laisser une rejection non gérée.
        .catch(() => {});
    } else {
      setIsInitializing(false);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    configureHttpClient({
      getAccessToken: () => sessionManager.getAccessToken(),
      onUnauthorized: async () => {
        const refreshToken = sessionManager.getRefreshToken();
        if (!refreshToken) return false;
        try {
          const response = await refreshRequest(refreshToken);
          sessionManager.setSession(response);
          return true;
        } catch {
          sessionManager.clearSession();
          return false;
        }
      },
    });
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await loginRequest(credentials);
    sessionManager.setSession(response);
  }, []);

  /**
   * Adoption d'une session issue du flux de paramétrage de compte (set-password
   * puis refresh) — le refresh renvoie une paire fraîche + l'email. Branchée
   * sur sessionManager.setSession (même modèle que login), elle déclenche le
   * re-rendu via useSyncExternalStore et rend le Bearer disponible à
   * configureHttpClient pour le PATCH profile qui suit.
   */
  const adoptSession = useCallback((response: AuthResponse) => {
    sessionManager.setSession(response);
  }, []);

  const logout = useCallback(() => {
    sessionManager.clearSession();
  }, []);

  const isAuthenticated = session !== null;
  const user = session ? { email: session.email } : null;

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isInitializing, login, adoptSession, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
